import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShieldCheck, AlertTriangle, RotateCcw, Save } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { rp, pct, fmtDate, ddmmyyyyToIso } from "../lib/format";
import { Logo } from "../components/brand/Logo";
import { CtaButton } from "../components/common";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import CurrencyInput from "../components/calc/CurrencyInput";
import ResultView from "../components/calc/ResultView";
import { AiSummary, DownloadActions } from "../components/calc/SimExtras";

const Step = ({ n, title, children, testid }) => (
  <section className="lp-card p-6" data-testid={testid}>
    <div className="mb-5 flex items-center gap-3">
      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#351080] text-xs font-bold text-white">{n}</span>
      <h2 className="font-display text-base font-bold text-[#17223B]">{title}</h2>
    </div>
    {children}
  </section>
);

const MaxRow = ({ k, v, strong, testid }) => (
  <div className="flex justify-between gap-4 border-b border-slate-100 py-2.5 text-sm last:border-0">
    <span className="text-slate-500">{k}</span><span className={`lp-num font-semibold ${strong ? "text-[#351080]" : "text-[#17223B]"}`} data-testid={testid}>{v}</span>
  </div>
);

function MaxCard({ mx }) {
  if (!mx.processable) return (
    <div className="flex gap-3 rounded-xl border border-red-200 bg-red-50 p-5 text-sm text-red-700" data-testid="max-stop-reason">
      <AlertTriangle className="h-5 w-5 shrink-0" /><div><p className="font-semibold">Pengajuan tidak dapat diproses</p><p>{mx.stop_reason}</p></div>
    </div>
  );
  return (
    <div className="lp-card p-6 lp-fade" data-testid="max-result">
      <p className="lp-eyebrow">Hasil Maksimal Simulasi</p>
      <p className="mt-1 font-display text-lg font-bold text-[#17223B]">Nama Debitur: {mx.name}</p>
      <div className="mt-4">
        <MaxRow k="Tanggal Proses" v={fmtDate(mx.process_date)} /><MaxRow k="Usia Saat Ini" v={`${mx.age} tahun`} testid="max-age" />
        <MaxRow k="Kategori Produk" v={mx.category} strong testid="max-category" /><MaxRow k="Bunga Koperasi" v={`${pct(mx.coop_rate)} per tahun`} testid="max-coop-rate" />
        <MaxRow k="Gaji Bulanan" v={rp(mx.salary)} /><MaxRow k="Angsuran Maksimal (95%)" v={rp(mx.max_installment)} testid="max-installment" />
        <MaxRow k="Tenor Maksimal" v={`${mx.max_tenor} bulan`} testid="max-tenor" /><MaxRow k="Plafond Maksimal" v={rp(mx.max_plafond)} strong testid="max-plafond" />
        <MaxRow k="Nilai Take Over" v={rp(mx.take_over)} />
      </div>
    </div>
  );
}

export default function CalculatorPage() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const leadId = sp.get("lead");
  const [banks, setBanks] = useState([]);
  const [bankKey, setBankKey] = useState("");
  const [dapem, setDapem] = useState("");
  const [phone, setPhone] = useState("");
  const [simStatus, setSimStatus] = useState("");
  const bank = banks.find((b) => b.key === bankKey);
  const [d, setD] = useState({ name: "", birth: "", salary: "", take_over: 0 });
  const [mx, setMx] = useState(null);
  const [tl, setTl] = useState("");
  const [mode, setMode] = useState("max");
  const [custom, setCustom] = useState({ plafond: "", tenor: "" });
  const [res, setRes] = useState(null);
  const [sim, setSim] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => { api.get("/banks").then((r) => setBanks(r.data.banks)); }, []);

  useEffect(() => {
    if (!leadId) return;
    api.get(`/leads/${leadId}`).then(({ data }) => setD((p) => ({ ...p, name: data.name, salary: data.pension_salary || "", birth: data.birth_date ? fmtDate(data.birth_date) : "" })))
      .catch(() => toast.error("Leads tidak ditemukan atau bukan milik Anda"));
  }, [leadId]);

  const base = () => ({ name: d.name, birth_date: ddmmyyyyToIso(d.birth), salary: Number(d.salary), take_over: Number(d.take_over || 0) });
  const reset = () => { setMx(null); setRes(null); setSim(null); setSimStatus(""); };

  const approve = async () => {
    setBusy("approve");
    try {
      const { data } = await api.post(`/simulations/${sim.id}/approve`, { phone: phone || null });
      toast.success(`Pengajuan ${data.app_no} dibuat. Lanjutkan upload dokumen.`);
      nav(`/progress?app=${data.id}`);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(""); }
  };
  const cancel = async () => {
    try { await api.post(`/simulations/${sim.id}/cancel`); setSimStatus("dibatalkan"); toast.info("Pengajuan dibatalkan. Simulasi tetap tersimpan."); } catch (err) { toast.error(errMsg(err)); }
  };

  const calcMax = async (e) => {
    e.preventDefault();
    if (!ddmmyyyyToIso(d.birth)) return toast.error("Tanggal lahir harus berformat DD/MM/YYYY");
    setBusy("max"); setRes(null); setSim(null);
    try { const { data } = await api.post("/calc/max", base()); setMx(data); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(""); }
  };

  const tlNum = tl === "" ? null : Number(String(tl).replace(",", "."));
  const tlError = tl !== "" && (isNaN(tlNum) || tlNum < 0 || tlNum > 3.5) ? "Tata Laksana tidak boleh negatif atau melebihi 3,5%." : "";

  const payload = () => ({
    ...base(), tata_laksana_pct: tlNum, lead_id: leadId || null, bank_key: bankKey, dapem: dapem || null,
    plafond: mode === "max" ? mx.max_plafond : Number(custom.plafond), tenor: mode === "max" ? mx.max_tenor : Number(custom.tenor),
  });

  const calcFinal = async () => {
    if (tl === "" || tlError) return toast.error(tlError || "Masukkan persentase Tata Laksana");
    if (mode === "custom" && (!custom.plafond || !custom.tenor)) return toast.error("Lengkapi plafond dan tenor");
    setBusy("final"); setSim(null);
    try { const { data } = await api.post("/calc/preview", payload()); setRes(data); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(""); }
  };

  const save = async () => {
    setBusy("save");
    try { const { data } = await api.post("/simulations", payload()); setSim(data); toast.success(`Simulasi ${data.sim_no} tersimpan`); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(""); }
  };

  return (
    <div>
      <div className="lp-card mb-8 flex flex-col gap-6 p-6 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
          <Logo size="md" data-testid="calc-header-logo" />
          <div className="sm:border-l sm:border-slate-200 sm:pl-5">
            <h1 className="font-display text-2xl font-extrabold tracking-tight text-[#351080]" data-testid="calc-title">LAYANAN PENSIUNAN SuperApps Navigator</h1>
            <p className="text-sm text-[#078D67]" data-testid="calc-subtitle">Your Business Partner. Growing Together. One Hand Solution.</p>
          </div>
        </div>
        <span className="inline-flex items-center gap-1.5 self-start rounded-full bg-[#078D67]/10 px-3 py-1 text-xs font-semibold text-[#078D67] md:self-center" data-testid="formula-status-badge">
          <ShieldCheck className="h-3.5 w-3.5" />Ketentuan AMS2 — Versi Final
        </span>
      </div>

      <section className="lp-card mb-6 p-6" data-testid="bank-select-step">
        <p className="lp-eyebrow mb-3">Pilih Bank / Mitra</p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-7">
          {banks.map((b) => (
            <button key={b.key} onClick={() => { setBankKey(b.key); reset(); }} data-testid={`bank-option-${b.key}`}
              className={`rounded-lg border px-3 py-3 text-left text-sm font-semibold transition-colors ${bankKey === b.key ? "border-[#5720B8] bg-[#5720B8]/8 text-[#351080]" : "border-slate-200 text-[#17223B] hover:border-[#5720B8]/40"}`}>
              {b.name}{!b.supported && <span className="mt-1 block text-[10px] font-medium text-slate-400">Dalam pengembangan</span>}
            </button>
          ))}
        </div>
        {bank && !bank.supported && (
          <div className="mt-4 flex gap-3 rounded-xl border border-[#F28A19]/30 bg-[#F28A19]/8 p-4 text-sm text-[#9A5200]" role="alert" data-testid="bank-dev-notice">
            <AlertTriangle className="h-5 w-5 shrink-0" /><div><p className="font-bold">Fitur Masih Dalam Pengembangan</p><p>Kalkulator {bank.name} belum tersedia. Silakan pilih mitra lain.</p></div>
          </div>
        )}
        {bank?.supported && (
          <div className="mt-4 flex flex-wrap items-center gap-4 text-sm">
            <span className="rounded-md bg-[#351080]/8 px-3 py-1.5 font-semibold text-[#351080]" data-testid="segment-badge">Segmen: PLATINUM (otomatis)</span>
            <label className="flex items-center gap-2">Jenis Dapem
              <select value={dapem} onChange={(e) => setDapem(e.target.value)} className="h-9 rounded-md border px-2" data-testid="dapem-select">
                <option value="">— Pilih —</option><option value="TASPEN">TASPEN</option><option value="ASABRI">ASABRI</option>
              </select>
            </label>
          </div>
        )}
      </section>

      {bank?.supported && <div className="grid gap-6 xl:grid-cols-[400px_1fr]">
        <div className="space-y-6">
          <Step n="1" title="Data Debitur" testid="calc-step-1">
            <form onSubmit={calcMax} className="space-y-4">
              <div className="space-y-1.5"><Label htmlFor="c-name">Nama debitur</Label><Input id="c-name" required className="h-11" value={d.name} onChange={(e) => { setD({ ...d, name: e.target.value }); reset(); }} data-testid="calc-name-input" /></div>
              <div className="space-y-1.5"><Label htmlFor="c-birth">Tanggal lahir (DD/MM/YYYY)</Label><Input id="c-birth" required placeholder="15/03/1950" className="h-11 lp-num" value={d.birth} onChange={(e) => { setD({ ...d, birth: e.target.value }); reset(); }} data-testid="calc-birth-input" /></div>
              <div className="space-y-1.5"><Label>Gaji per bulan</Label><CurrencyInput required value={d.salary} onChange={(v) => { setD({ ...d, salary: v }); reset(); }} data-testid="calc-salary-input" /></div>
              <div className="space-y-1.5"><Label>Nilai Take Over (TO)</Label><CurrencyInput value={d.take_over} onChange={(v) => { setD({ ...d, take_over: v }); reset(); }} data-testid="calc-takeover-input" /></div>
              <CtaButton type="submit" className="h-11 w-full font-semibold" disabled={busy === "max"} data-testid="calc-max-button">
                {busy === "max" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Hitung Hasil Maksimal"}
              </CtaButton>
            </form>
          </Step>

          {mx?.processable && (
            <Step n="2" title="Tata Laksana, Plafond & Tenor" testid="calc-step-2">
              <div className="space-y-5">
                <div className="space-y-1.5">
                  <Label htmlFor="c-tl">Tata Laksana (%)</Label>
                  <Input id="c-tl" inputMode="decimal" placeholder="0 – 3,5" className="h-11" value={tl} onChange={(e) => { setTl(e.target.value); setRes(null); setSim(null); }} data-testid="calc-tl-input" />
                  <p className={`text-xs ${tlError ? "text-red-600" : "text-slate-500"}`} data-testid="calc-tl-hint">{tlError || "Standar maksimal Tata Laksana adalah 3,5% dari plafond pengajuan. Tidak boleh negatif."}</p>
                </div>
                <RadioGroup value={mode} onValueChange={(v) => { setMode(v); setRes(null); setSim(null); }} className="gap-3">
                  <label className="flex cursor-pointer items-center gap-3 rounded-lg border p-3 text-sm has-[:checked]:border-[#5720B8]"><RadioGroupItem value="max" data-testid="calc-mode-max" />Gunakan plafond & tenor maksimal</label>
                  <label className="flex cursor-pointer items-center gap-3 rounded-lg border p-3 text-sm has-[:checked]:border-[#5720B8]"><RadioGroupItem value="custom" data-testid="calc-mode-custom" />Tentukan plafond & tenor sendiri</label>
                </RadioGroup>
                {mode === "custom" && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2 space-y-1.5"><Label>Plafond</Label><CurrencyInput value={custom.plafond} onChange={(v) => { setCustom({ ...custom, plafond: v }); setRes(null); }} data-testid="calc-plafond-input" /></div>
                    <div className="space-y-1.5"><Label htmlFor="c-tenor">Tenor (bulan)</Label><Input id="c-tenor" inputMode="numeric" className="h-11" value={custom.tenor} onChange={(e) => { setCustom({ ...custom, tenor: e.target.value.replace(/\D/g, "") }); setRes(null); }} data-testid="calc-tenor-input" /></div>
                  </div>
                )}
                <Button onClick={calcFinal} disabled={busy === "final"} className="h-11 w-full bg-[#351080] font-semibold hover:bg-[#2a0c66]" data-testid="calc-final-button">
                  {busy === "final" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Hitung Simulasi Final"}
                </Button>
              </div>
            </Step>
          )}
        </div>

        <div className="space-y-6">
          {!mx && (
            <div className="lp-card flex min-h-[320px] flex-col items-center justify-center p-10 text-center">
              <Logo variant="vertical" size="sm" className="opacity-90" />
              <p className="mt-6 max-w-sm text-sm text-slate-500">Masukkan nama, tanggal lahir, gaji per bulan, dan nilai Take Over untuk memulai simulasi.</p>
            </div>
          )}
          {mx && <MaxCard mx={mx} />}
          {res && (
            <div className="lp-card p-6 lp-fade">
              <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                <div><p className="lp-eyebrow">Your Business Partner. Growing Together. One Hand Solution.</p><h2 className="font-display text-xl font-bold text-[#17223B]">Hasil Simulasi Final</h2></div>
                {sim && <span className="rounded-md bg-[#351080]/8 px-3 py-1 text-sm font-semibold text-[#351080]" data-testid="sim-number">{sim.sim_no}</span>}
              </div>
              {!res.overall_ok && (
                <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-testid="final-issues">
                  <p className="font-semibold">Pengajuan belum sesuai ketentuan. Mohon koreksi nilai pengajuan:</p>
                  <ul className="mt-1 list-disc pl-5">{res.issues.map((i) => <li key={i}>{i}</li>)}</ul>
                </div>
              )}
              <ResultView r={res} />
              <div className="mt-6 space-y-4">
                {res.overall_ok && !sim && (
                  <CtaButton onClick={save} disabled={busy === "save"} className="h-11 w-full font-semibold" data-testid="calc-save-button">
                    {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-2 h-4 w-4" />Simpan Simulasi</>}
                  </CtaButton>
                )}
                {sim && <><DownloadActions sim={sim} />
                  <div className="rounded-xl border border-[#351080]/15 p-4" data-testid="sim-approval-panel">
                    <p className="font-display text-sm font-bold text-[#351080]">Persetujuan Simulasi</p>
                    {simStatus === "dibatalkan" ? <p className="mt-2 text-sm text-slate-500" data-testid="sim-cancelled">Pengajuan dibatalkan.</p> : <>
                      {!leadId && <div className="mt-3 max-w-xs space-y-1.5"><Label htmlFor="c-phone">No. telepon calon debitur (untuk data leads)</Label>
                        <Input id="c-phone" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="approve-phone-input" /></div>}
                      <div className="mt-3 flex flex-wrap gap-2">
                        <Button onClick={approve} disabled={busy === "approve"} className="bg-[#078D67] hover:bg-[#067556]" data-testid="approve-sim-button">Setujui Simulasi &amp; Lanjut Pengajuan</Button>
                        <Button variant="outline" onClick={cancel} className="text-red-600" data-testid="cancel-sim-button">Batalkan Pengajuan</Button>
                      </div>
                    </>}
                  </div>
                  <AiSummary sim={sim} /></>}
                <Button variant="ghost" onClick={() => { setD({ name: "", birth: "", salary: "", take_over: 0 }); setTl(""); setMode("max"); setCustom({ plafond: "", tenor: "" }); reset(); }} data-testid="calc-reset-button">
                  <RotateCcw className="mr-2 h-4 w-4" />Simulasi baru
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>}
    </div>
  );
}
